import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { alarmService, CreateAlarmData, UpdateAlarmData } from '@/services/alarmService';
import { Alarm } from '@/types';
import { formatDate } from '@/utils';

export function AlarmsManagementPage() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'OPEN' | 'CLOSED' | undefined>(undefined);
  const itemsPerPage = 20;

  // Fetch alarms
  const {
    data: paginatedResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['alarms', currentPage, statusFilter],
    queryFn: () => alarmService.getAlarms({ page: currentPage, itemsPerPage, status: statusFilter }),
  });

  const alarms = paginatedResponse?.data || [];
  const totalItems = paginatedResponse?.totalItems || 0;
  const totalPages = paginatedResponse?.totalPages || 0;

  // Update alarm status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'OPEN' | 'CLOSED' }) =>
      alarmService.updateAlarm(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alarms'] });
    },
  });

  const handleStatusChange = (alarm: Alarm, newStatus: 'OPEN' | 'CLOSED') => {
    updateStatusMutation.mutate({ id: alarm.id, status: newStatus });
  };

  const getStatusBadge = (status: string) => {
    return status === 'OPEN' ? (
      <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
        Ouverte
      </span>
    ) : (
      <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
        Fermée
      </span>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 hidden md:flex flex-col bg-white dark:bg-[#16202A] border-r border-slate-200 dark:border-[#1E293B]">
        <div className="h-16 flex items-center px-6 border-b border-slate-200 dark:border-[#1E293B]">
          <div className="flex items-center gap-2 text-primary font-bold text-xl">
            <span className="material-icons-outlined">admin_panel_settings</span>
            <span>GTB Admin</span>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          <a
            href="/admin/dashboard"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#1E293B] transition-colors"
          >
            <span className="material-icons-outlined">dashboard</span>
            <span>Tableau de bord</span>
          </a>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-white dark:bg-[#16202A] border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between px-6">
          <h1 className="text-xl font-semibold">Gestion des Alarmes</h1>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {/* Filters */}
          <div className="mb-6 flex gap-3">
            <button
              onClick={() => setStatusFilter(undefined)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === undefined
                  ? 'bg-primary text-white'
                  : 'bg-white dark:bg-[#16202A] border border-slate-300 dark:border-[#1E293B] text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#1E293B]'
              }`}
            >
              Toutes
            </button>
            <button
              onClick={() => setStatusFilter('OPEN')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'OPEN'
                  ? 'bg-red-500 text-white'
                  : 'bg-white dark:bg-[#16202A] border border-slate-300 dark:border-[#1E293B] text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#1E293B]'
              }`}
            >
              Ouvertes
            </button>
            <button
              onClick={() => setStatusFilter('CLOSED')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'CLOSED'
                  ? 'bg-green-500 text-white'
                  : 'bg-white dark:bg-[#16202A] border border-slate-300 dark:border-[#1E293B] text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#1E293B]'
              }`}
            >
              Fermées
            </button>
          </div>

          {/* Alarms Table */}
          {isLoading ? (
            <div className="text-center py-12">Chargement...</div>
          ) : error ? (
            <div className="text-center py-12 text-red-500">Erreur lors du chargement des alarmes</div>
          ) : alarms.length === 0 ? (
            <div className="text-center py-12 text-slate-500">Aucune alarme trouvée</div>
          ) : (
            <>
              <div className="bg-white dark:bg-[#16202A] rounded-lg border border-slate-200 dark:border-[#1E293B] overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-[#1E293B]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Titre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Valeur actuelle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Valeur normale
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Créée le
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-[#1E293B]">
                    {alarms.map((alarm) => {
                      const alarmType = typeof alarm.type === 'object' ? alarm.type : null;
                      return (
                        <tr key={alarm.id} className="hover:bg-slate-50 dark:hover:bg-[#1E293B]">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{alarm.title}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {alarmType?.label || alarmType?.code || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{alarm.currentValue || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{alarm.normaleValue}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(alarm.status)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                            {formatDate(alarm.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              {alarm.status === 'OPEN' ? (
                                <button
                                  onClick={() => handleStatusChange(alarm, 'CLOSED')}
                                  className="text-green-500 hover:text-green-700"
                                  title="Fermer l'alarme"
                                  disabled={updateStatusMutation.isPending}
                                >
                                  <span className="material-icons-outlined">check_circle</span>
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleStatusChange(alarm, 'OPEN')}
                                  className="text-orange-500 hover:text-orange-700"
                                  title="Rouvrir l'alarme"
                                  disabled={updateStatusMutation.isPending}
                                >
                                  <span className="material-icons-outlined">refresh</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-slate-500">
                    Affichage de {(currentPage - 1) * itemsPerPage + 1} à{' '}
                    {Math.min(currentPage * itemsPerPage, totalItems)} sur {totalItems} alarmes
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-[#1E293B]"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-[#1E293B]"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
